import { useState, useCallback, useEffect } from 'react';
import { useDropzone } from 'react-dropzone';
import { supabase, validateSession } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';

interface ImageUploadProps {
  onImageUploaded?: (url: string) => void;
  onImageUpload?: (url: string) => void; // Alias for single image mode
  onImageRemoved?: (url: string) => void;
  maxFiles?: number;
  maxSize?: number;
  className?: string;
  uploadedUrls?: string[];
  currentImage?: string; // For single image mode
  aspectRatio?: string; // e.g., "16:9", "1:1", "4:3"
}

interface UploadingImage {
  file: File;
  preview: string;
  status: 'uploading' | 'success' | 'error';
  url?: string;
  error?: string;
  progress?: number;
}

export default function ImageUpload({ 
  onImageUploaded, 
  onImageUpload,
  onImageRemoved,
  maxFiles = 5, 
  maxSize = 5 * 1024 * 1024, // 5MB
  className = "",
  uploadedUrls = [],
  currentImage,
  aspectRatio
}: ImageUploadProps) {
  // Determine if single image mode
  const isSingleMode = !!onImageUpload || !!currentImage || maxFiles === 1;
  const effectiveMaxFiles = isSingleMode ? 1 : maxFiles;
  
  // Handle callback - support both prop names
  const handleImageCallback = onImageUpload || onImageUploaded;

  const [uploadingImages, setUploadingImages] = useState<UploadingImage[]>([]);
  const [uploadedImages, setUploadedImages] = useState<string[]>(() => {
    if (currentImage) return [currentImage];
    return uploadedUrls;
  });
  const [error, setError] = useState<string>('');
  const { user, session, isAuthenticated } = useAuth();

  // Sync with currentImage prop changes
  useEffect(() => {
    if (currentImage) {
      setUploadedImages([currentImage]);
    } else if (isSingleMode && !currentImage) {
      setUploadedImages([]);
    }
  }, [currentImage, isSingleMode]);

  // Sync with uploadedUrls prop changes
  useEffect(() => {
    if (!isSingleMode && uploadedUrls.length > 0) {
      setUploadedImages(uploadedUrls);
    }
  }, [uploadedUrls, isSingleMode]);

  const uploadImage = async (file: File, index: number): Promise<string> => {
    if (!isAuthenticated || !user || !session) {
      throw new Error('Please sign in to upload images');
    }

    const validSession = await validateSession();
    if (!validSession) {
      throw new Error('Session expired. Please sign in again');
    }

    const fileExt = file.name.split('.').pop();
    const fileName = `${Date.now()}-${Math.random().toString(36).substring(2)}.${fileExt}`;
    const filePath = `post-images/${user.id}/${fileName}`;

    // Update progress
    setUploadingImages(prev => prev.map((img, i) => 
      i === index ? { ...img, progress: 50 } : img
    ));

    // Upload the file
    const { error: uploadError } = await supabase.storage
      .from('images')
      .upload(filePath, file, {
        cacheControl: '3600',
        upsert: false,
        metadata: {
          userId: user.id,
          uploadedAt: new Date().toISOString()
        }
      });

    if (uploadError) {
      console.error('Upload error:', uploadError);
      
      if (uploadError.message.includes('row-level security') || uploadError.message.includes('RLS')) {
        throw new Error('Upload permission denied. Please try signing out and back in.');
      }
      
      if (uploadError.message.includes('duplicate')) {
        const retryFileName = `${Date.now()}-${Math.random().toString(36).substring(2)}-retry.${fileExt}`;
        const retryFilePath = `post-images/${user.id}/${retryFileName}`;
        
        const { error: retryError } = await supabase.storage
          .from('images')
          .upload(retryFilePath, file, {
            cacheControl: '3600',
            upsert: false
          });
          
        if (retryError) {
          throw new Error(`Upload failed: ${retryError.message}`);
        }
        
        const { data: retryUrlData } = supabase.storage
          .from('images')
          .getPublicUrl(retryFilePath);
          
        return retryUrlData.publicUrl;
      }
      
      throw new Error(`Upload failed: ${uploadError.message}`);
    }

    const { data: urlData } = supabase.storage
      .from('images')
      .getPublicUrl(filePath);

    if (!urlData.publicUrl) {
      throw new Error('Failed to get image URL');
    }

    // Update progress to 100%
    setUploadingImages(prev => prev.map((img, i) => 
      i === index ? { ...img, progress: 100 } : img
    ));

    return urlData.publicUrl;
  };

  const onDrop = useCallback(async (acceptedFiles: File[]) => {
    if (!isAuthenticated || !user) {
      setError('Please sign in to upload images');
      return;
    }

    const totalImages = uploadedImages.length + uploadingImages.length + acceptedFiles.length;
    if (totalImages > effectiveMaxFiles) {
      setError(`Maximum ${effectiveMaxFiles} image${effectiveMaxFiles > 1 ? 's' : ''} allowed`);
      return;
    }

    const oversizedFiles = acceptedFiles.filter(file => file.size > maxSize);
    if (oversizedFiles.length > 0) {
      setError(`Each image must be less than ${Math.round(maxSize / (1024 * 1024))}MB`);
      return;
    }

    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
    const invalidFiles = acceptedFiles.filter(file => !allowedTypes.includes(file.type));
    if (invalidFiles.length > 0) {
      setError('Only JPEG, PNG, GIF, and WebP images are allowed');
      return;
    }

    setError('');

    try {
      if (!session) {
        throw new Error('Authentication required. Please sign in.');
      }

      // Create preview URLs and add to uploading state
      const newUploadingImages: UploadingImage[] = acceptedFiles.map(file => ({
        file,
        preview: URL.createObjectURL(file),
        status: 'uploading',
        progress: 0
      }));

      if (isSingleMode) {
        // In single mode, replace existing
        setUploadingImages(newUploadingImages);
        setUploadedImages([]);
      } else {
        setUploadingImages(prev => [...prev, ...newUploadingImages]);
      }

      // Upload files one by one
      const startIndex = isSingleMode ? 0 : uploadingImages.length;
      for (let i = 0; i < acceptedFiles.length; i++) {
        const file = acceptedFiles[i];
        const uploadIndex = startIndex + i;
        
        try {
          const url = await uploadImage(file, uploadIndex);
          
          // Update status to success
          setUploadingImages(prev => prev.map((img, idx) => 
            idx === uploadIndex ? { ...img, status: 'success', url } : img
          ));

          // Add to uploaded images
          if (isSingleMode) {
            setUploadedImages([url]);
            if (handleImageCallback) {
              handleImageCallback(url);
            }
          } else {
            setUploadedImages(prev => [...prev, url]);
            if (handleImageCallback) {
              handleImageCallback(url);
            }
          }

          // Remove from uploading after a short delay
          setTimeout(() => {
            setUploadingImages(prev => prev.filter((_, idx) => idx !== uploadIndex));
          }, 1000);

        } catch (err: any) {
          console.error('Upload error:', err);
          
          // Update status to error
          setUploadingImages(prev => prev.map((img, idx) => 
            idx === uploadIndex ? { ...img, status: 'error', error: err.message } : img
          ));
        }
      }
    } catch (err: any) {
      console.error('Upload error:', err);
      
      if (err.message.includes('sign in') || err.message.includes('Authentication') || err.message.includes('Session expired')) {
        setError('Please sign in to upload images');
      } else if (err.message.includes('permission') || err.message.includes('RLS') || err.message.includes('row-level security')) {
        setError('Upload permission denied. Please try signing out and back in.');
      } else {
        setError(err.message || 'Failed to upload images');
      }
    }
  }, [uploadedImages.length, uploadingImages.length, effectiveMaxFiles, maxSize, handleImageCallback, isAuthenticated, user, session, isSingleMode]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'image/*': ['.jpeg', '.jpg', '.png', '.gif', '.webp']
    },
    maxSize,
    disabled: (uploadedImages.length + uploadingImages.length) >= effectiveMaxFiles || !isAuthenticated
  });

  const removeImage = (indexToRemove: number) => {
    const removedUrl = uploadedImages[indexToRemove];
    setUploadedImages(prev => prev.filter((_, index) => index !== indexToRemove));
    if (onImageRemoved) {
      onImageRemoved(removedUrl);
    }
    // In single mode, also call the upload callback with empty string to clear
    if (isSingleMode && handleImageCallback) {
      handleImageCallback('');
    }
  };

  const removeUploadingImage = (indexToRemove: number) => {
    setUploadingImages(prev => {
      const img = prev[indexToRemove];
      if (img.preview) {
        URL.revokeObjectURL(img.preview);
      }
      return prev.filter((_, index) => index !== indexToRemove);
    });
  };

  const retryUpload = async (index: number) => {
    const img = uploadingImages[index];
    if (!img || img.status !== 'error') return;

    setUploadingImages(prev => prev.map((item, i) => 
      i === index ? { ...item, status: 'uploading', error: undefined, progress: 0 } : item
    ));

    try {
      const url = await uploadImage(img.file, index);
      
      setUploadingImages(prev => prev.map((item, i) => 
        i === index ? { ...item, status: 'success', url } : item
      ));

      if (isSingleMode) {
        setUploadedImages([url]);
        if (handleImageCallback) {
          handleImageCallback(url);
        }
      } else {
        setUploadedImages(prev => [...prev, url]);
        if (handleImageCallback) {
          handleImageCallback(url);
        }
      }

      setTimeout(() => {
        setUploadingImages(prev => prev.filter((_, idx) => idx !== index));
      }, 1000);

    } catch (err: any) {
      setUploadingImages(prev => prev.map((item, i) => 
        i === index ? { ...item, status: 'error', error: err.message } : item
      ));
    }
  };

  // Show sign-in message if not authenticated
  if (!isAuthenticated) {
    return (
      <div className={className}>
        <div className="border-2 border-dashed border-gray-200 rounded-lg p-6 text-center bg-gray-50">
          <i className="ri-user-line text-2xl text-gray-400 mb-2"></i>
          <p className="text-sm text-gray-600">Please sign in to upload images</p>
        </div>
      </div>
    );
  }

  // Single image mode with cover image preview
  if (isSingleMode) {
    return (
      <div className={className}>
        {uploadedImages.length > 0 && uploadedImages[0] ? (
          <div className="relative rounded-lg overflow-hidden border border-gray-200">
            <div className={`relative w-full ${aspectRatio === '16:9' ? 'pb-[56.25%]' : aspectRatio === '1:1' ? 'pb-[100%]' : aspectRatio === '4:3' ? 'pb-[75%]' : 'pb-[56.25%]'}`}>
              <img
                src={uploadedImages[0]}
                alt="Cover"
                className="absolute inset-0 w-full h-full object-cover"
              />
            </div>
            <div className="absolute inset-0 bg-black/0 hover:bg-black/40 transition-colors group">
              <div className="absolute inset-0 flex items-center justify-center gap-3 opacity-0 group-hover:opacity-100 transition-opacity">
                <button
                  type="button"
                  onClick={() => removeImage(0)}
                  className="px-4 py-2 bg-red-500 text-white rounded-lg text-sm font-medium hover:bg-red-600 transition-colors cursor-pointer"
                >
                  <i className="ri-delete-bin-line mr-1"></i>
                  Remove
                </button>
              </div>
            </div>
          </div>
        ) : uploadingImages.length > 0 ? (
          <div className="relative rounded-lg overflow-hidden border border-gray-200">
            <div className={`relative w-full ${aspectRatio === '16:9' ? 'pb-[56.25%]' : aspectRatio === '1:1' ? 'pb-[100%]' : aspectRatio === '4:3' ? 'pb-[75%]' : 'pb-[56.25%]'}`}>
              <img
                src={uploadingImages[0].preview}
                alt="Uploading"
                className="absolute inset-0 w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                {uploadingImages[0].status === 'uploading' && (
                  <div className="text-center">
                    <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-white mb-2 mx-auto"></div>
                    <p className="text-sm text-white">Uploading...</p>
                  </div>
                )}
                {uploadingImages[0].status === 'error' && (
                  <div className="text-center">
                    <i className="ri-error-warning-line text-3xl text-red-400 mb-2"></i>
                    <p className="text-sm text-white mb-2">{uploadingImages[0].error}</p>
                    <button
                      type="button"
                      onClick={() => retryUpload(0)}
                      className="px-3 py-1 bg-white text-gray-900 rounded text-sm cursor-pointer"
                    >
                      Retry
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        ) : (
          <div
            {...getRootProps()}
            className={`border-2 border-dashed rounded-lg text-center cursor-pointer transition-colors ${
              isDragActive
                ? 'border-teal-400 bg-teal-50'
                : 'border-gray-300 hover:border-teal-400 hover:bg-teal-50/50'
            }`}
          >
            <input {...getInputProps()} />
            <div className={`relative w-full ${aspectRatio === '16:9' ? 'pb-[56.25%]' : aspectRatio === '1:1' ? 'pb-[100%]' : aspectRatio === '4:3' ? 'pb-[75%]' : 'pb-[56.25%]'}`}>
              <div className="absolute inset-0 flex flex-col items-center justify-center">
                <div className="w-12 h-12 rounded-full bg-teal-100 flex items-center justify-center mb-3">
                  <i className="ri-image-add-line text-2xl text-teal-600"></i>
                </div>
                <p className="text-sm text-gray-700 font-medium mb-1">
                  {isDragActive ? 'Drop image here' : 'Click or drag to upload cover image'}
                </p>
                <p className="text-xs text-gray-500">
                  PNG, JPG, GIF, WebP up to {Math.round(maxSize / (1024 * 1024))}MB
                </p>
              </div>
            </div>
          </div>
        )}

        {error && (
          <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded text-sm text-red-600">
            {error}
          </div>
        )}
      </div>
    );
  }

  // Multi-image mode (original behavior)
  return (
    <div className={className}>
      {/* Upload Area */}
      <div
        {...getRootProps()}
        className={`border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors ${
          isDragActive
            ? 'border-blue-400 bg-blue-50'
            : (uploadedImages.length + uploadingImages.length) >= effectiveMaxFiles
            ? 'border-gray-200 bg-gray-50 cursor-not-allowed'
            : 'border-gray-300 hover:border-gray-400'
        }`}
      >
        <input {...getInputProps()} />
        
        {(uploadedImages.length + uploadingImages.length) >= effectiveMaxFiles ? (
          <div className="flex flex-col items-center">
            <i className="ri-image-line text-2xl text-gray-400 mb-2"></i>
            <p className="text-sm text-gray-500">Maximum images reached ({effectiveMaxFiles})</p>
          </div>
        ) : (
          <div className="flex flex-col items-center">
            <i className="ri-upload-cloud-2-line text-2xl text-gray-400 mb-2"></i>
            <p className="text-sm text-gray-600 mb-1">
              {isDragActive ? 'Drop images here' : 'Drag & drop images here, or click to select'}
            </p>
            <p className="text-xs text-gray-500">
              PNG, JPG, GIF, WebP up to {Math.round(maxSize / (1024 * 1024))}MB ({uploadedImages.length + uploadingImages.length}/{effectiveMaxFiles})
            </p>
          </div>
        )}
      </div>

      {/* Error Message */}
      {error && (
        <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded text-sm text-red-600">
          {error}
        </div>
      )}

      {/* Uploading Images Preview */}
      {uploadingImages.length > 0 && (
        <div className="mt-4">
          <h4 className="text-sm font-medium text-gray-700 mb-2">Uploading:</h4>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
            {uploadingImages.map((img, index) => (
              <div key={index} className="relative group">
                <div className="relative w-full h-24 rounded border overflow-hidden">
                  <img
                    src={img.preview}
                    alt={`Uploading ${index + 1}`}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                    {img.status === 'uploading' && (
                      <div className="text-center">
                        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-white mb-1 mx-auto"></div>
                        <p className="text-xs text-white">{img.progress || 0}%</p>
                      </div>
                    )}
                    {img.status === 'success' && (
                      <div className="text-center">
                        <i className="ri-check-line text-2xl text-green-400"></i>
                      </div>
                    )}
                    {img.status === 'error' && (
                      <div className="text-center">
                        <i className="ri-error-warning-line text-xl text-red-400 mb-1"></i>
                        <button
                          type="button"
                          onClick={() => retryUpload(index)}
                          className="px-2 py-0.5 bg-white text-gray-900 rounded text-xs cursor-pointer"
                        >
                          Retry
                        </button>
                      </div>
                    )}
                  </div>
                </div>
                {img.status === 'error' && (
                  <button
                    type="button"
                    onClick={() => removeUploadingImage(index)}
                    className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs cursor-pointer z-10"
                  >
                    ×
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Uploaded Images Preview */}
      {uploadedImages.length > 0 && (
        <div className="mt-4">
          <h4 className="text-sm font-medium text-gray-700 mb-2">Uploaded Images:</h4>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
            {uploadedImages.map((url, index) => (
              <div key={index} className="relative group">
                <img
                  src={url}
                  alt={`Upload ${index + 1}`}
                  className="w-full h-24 object-cover rounded border"
                />
                <button
                  type="button"
                  onClick={() => removeImage(index)}
                  className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer"
                >
                  ×
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
